import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Gift, 
  Percent, 
  DollarSign, 
  Star, 
  Award,
  Crown,
  Lock,
  CheckCircle,
  Clock,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';

const BENEFIT_ICONS = {
  discount_percentage: Percent,
  discount_fixed: DollarSign,
  free_service: Gift,
  priority_access: Star,
  coupon: Award,
  other: Sparkles
};

// Tier breakpoints: 0-3, 3-6, 6-9, 9-12, +12
const getTierInfo = (months, t) => {
  if (months <= 3) return { name: t('benefits.tiers.bronze'), label: '0-3 ' + t('tabs.history').toLowerCase(), color: 'bg-amber-100 text-amber-800', icon: Award };
  if (months <= 6) return { name: t('benefits.tiers.silver'), label: '3-6 ' + t('tabs.history').toLowerCase(), color: 'bg-gray-200 text-gray-800', icon: Award };
  if (months <= 9) return { name: t('benefits.tiers.gold'), label: '6-9 ' + t('tabs.history').toLowerCase(), color: 'bg-yellow-100 text-yellow-800', icon: Star };
  if (months <= 12) return { name: t('benefits.tiers.platinum'), label: '9-12 ' + t('tabs.history').toLowerCase(), color: 'bg-purple-100 text-purple-800', icon: Crown };
  return { name: t('benefits.tiers.diamond'), label: '+12 ' + t('tabs.history').toLowerCase(), color: 'bg-blue-100 text-blue-800', icon: Crown };
};

const getNextTierInfo = (currentMonths, t) => {
  if (currentMonths < 3) return { name: t('benefits.tiers.silver'), months: 3, color: 'bg-gray-200' };
  if (currentMonths < 6) return { name: t('benefits.tiers.gold'), months: 6, color: 'bg-yellow-100' };
  if (currentMonths < 9) return { name: t('benefits.tiers.platinum'), months: 9, color: 'bg-purple-100' };
  if (currentMonths < 12) return { name: t('benefits.tiers.diamond'), months: 12, color: 'bg-blue-100' };
  return null; // Already at max tier
};

const MemberBenefits = ({ member }) => {
  const { t } = useTranslation();
  const [benefitsData, setBenefitsData] = useState(null);
  const [planBenefits, setPlanBenefits] = useState([]);
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (member?.id) {
      fetchBenefits();
    }
  }, [member?.id]);

  const fetchBenefits = async () => {
    setLoading(true);
    try {
      const [benefitsRes, historyRes, fulfillmentRes] = await Promise.all([
        axios.get(`${API}/benefits/member/${member.id}`).catch(() => ({ data: { benefits: [] } })),
        axios.get(`${API}/benefits/usage/member/${member.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/fulfillment/my-membership`).catch(() => ({ data: { plan_benefits: [], plan_name: '', plan_description: '' } }))
      ]);
      setBenefitsData(benefitsRes.data);
      setUsageHistory(historyRes.data);
      
      // Get plan benefits from fulfillment API
      if (fulfillmentRes.data) {
        setPlanBenefits(fulfillmentRes.data.plan_benefits || []);
        setPlanName(fulfillmentRes.data.plan_name || '');
        setPlanDescription(fulfillmentRes.data.plan_description || '');
      }
    } catch (error) {
      console.error('Error fetching benefits:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (benefit) => {
    if (!benefit.value) return '';
    switch (benefit.benefit_type) {
      case 'discount_percentage':
        return `${benefit.value}% off`;
      case 'discount_fixed':
        return `$${benefit.value} off`;
      default:
        return benefit.value;
    }
  };

  const getUsageDisplay = (benefit, t) => {
    if (benefit.quantity_allowed === -1) {
      return { text: t('benefits.unlimited'), percentage: 0 };
    }
    const percentage = (benefit.quantity_used / benefit.quantity_allowed) * 100;
    return {
      text: t('benefits.usageInfo', { used: benefit.quantity_used, total: benefit.quantity_allowed }),
      percentage: Math.min(100, percentage)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!benefitsData || benefitsData.benefits.length === 0) {
    // Show plan benefits if available, even when there are no tier benefits
    if (planBenefits.length > 0) {
      return (
        <div className="space-y-6">
          {/* Plan Benefits Card */}
          <Card className="bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-teal-600" />
                {t('benefits.yourPlanBenefits', 'Your Plan Benefits')}
              </CardTitle>
              <CardDescription>
                {planName && <span className="font-medium text-teal-700">{planName}</span>}
                {planDescription && <span className="text-gray-600 ml-2">- {planDescription}</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3">
                {planBenefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-teal-100 shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <span className="text-gray-800 font-medium">{benefit}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Info Card about Loyalty Benefits */}
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800">{t('benefits.loyaltyProgram', 'Loyalty Rewards Coming Soon')}</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    {t('benefits.loyaltyDescription', 'As you continue your membership, you\'ll unlock additional exclusive benefits and rewards!')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Gift className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">{t('benefits.noBenefitsAvailable')}</p>
          <p className="text-sm text-gray-400 mt-2">
            {t('benefits.benefitsWillAppear')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const membershipMonths = benefitsData.membership_months || 0;
  const currentTier = getTierInfo(membershipMonths, t);
  const nextTier = getNextTierInfo(membershipMonths, t);
  const unlockedBenefits = benefitsData.benefits.filter(b => b.is_unlocked);
  const lockedBenefits = benefitsData.benefits.filter(b => !b.is_unlocked);

  return (
    <div className="space-y-6">
      {/* Plan Benefits - Always show at top if available */}
      {planBenefits.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gift className="h-5 w-5 text-teal-600" />
              {t('benefits.yourPlanIncludes', 'Your Plan Includes')}
            </CardTitle>
            {planName && (
              <CardDescription className="text-teal-700 font-medium">{planName}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-2">
              {planBenefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-teal-50 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Membership Tier Card */}
      <Card className="bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center">
                <currentTier.icon className="h-8 w-8 text-teal-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{t('benefits.memberTier', { tier: currentTier.name })}</h3>
                <p className="text-gray-600">
                  {t('benefits.monthsOfMembership', { count: membershipMonths })}
                </p>
                <Badge className={currentTier.color + ' mt-1'}>
                  {currentTier.label}
                </Badge>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{t('benefits.availableBenefits')}</p>
              <p className="text-2xl font-bold text-teal-600">{unlockedBenefits.length}</p>
            </div>
          </div>
          
          {/* Next tier progress */}
          {nextTier && (
            <div className="mt-4 pt-4 border-t border-teal-200">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">
                  {t('benefits.progressToTier', { tier: nextTier.name })}
                </span>
                <span className="font-medium text-teal-700">
                  {t('benefits.monthsToGo', { count: nextTier.months - membershipMonths })}
                </span>
              </div>
              <Progress 
                value={(membershipMonths / nextTier.months) * 100} 
                className="h-2"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="available" className="space-y-4">
        <TabsList>
          <TabsTrigger value="available">
            {t('tabs.available')} ({unlockedBenefits.length})
          </TabsTrigger>
          <TabsTrigger value="upcoming">
            {t('tabs.upcoming')} ({lockedBenefits.length})
          </TabsTrigger>
          <TabsTrigger value="history">
            {t('tabs.usageHistory')}
          </TabsTrigger>
        </TabsList>

        {/* Available Benefits */}
        <TabsContent value="available">
          <div className="grid gap-4">
            {unlockedBenefits.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Gift className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{t('benefits.noBenefitsAvailable')}</p>
                </CardContent>
              </Card>
            ) : (
              unlockedBenefits.map((benefit) => {
                const Icon = BENEFIT_ICONS[benefit.benefit_type] || Sparkles;
                const usage = getUsageDisplay(benefit, t);
                const isExhausted = benefit.quantity_remaining === 0;
                
                return (
                  <Card 
                    key={benefit.benefit_id}
                    className={`border-l-4 ${isExhausted ? 'border-l-gray-300 opacity-60' : 'border-l-teal-500'}`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isExhausted ? 'bg-gray-100' : 'bg-teal-100'}`}>
                            <Icon className={`h-5 w-5 ${isExhausted ? 'text-gray-400' : 'text-teal-600'}`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{benefit.benefit_name}</h4>
                            {benefit.description && (
                              <p className="text-sm text-gray-500 mt-1">{benefit.description}</p>
                            )}
                            {benefit.value && (
                              <Badge className="mt-2 bg-teal-100 text-teal-800">
                                {formatValue(benefit)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {isExhausted ? (
                            <Badge className="bg-gray-100 text-gray-600">
                              <Clock className="h-3 w-3 mr-1" />
                              {t('benefits.exhausted')}
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {t('serviceStatus.available')}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {/* Usage Progress */}
                      {benefit.quantity_allowed !== -1 && (
                        <div className="mt-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{usage.text}</span>
                            <span className="font-medium">
                              {benefit.quantity_remaining} {t('membershipCenter.remaining')}
                            </span>
                          </div>
                          <Progress value={usage.percentage} className="h-2" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Upcoming Benefits (Locked) */}
        <TabsContent value="upcoming">
          <div className="grid gap-4">
            {lockedBenefits.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Crown className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{t('benefits.noBenefitsAvailable')}</p>
                </CardContent>
              </Card>
            ) : (
              lockedBenefits.map((benefit) => {
                const Icon = BENEFIT_ICONS[benefit.benefit_type] || Sparkles;
                const tierInfo = getTierInfo(benefit.unlock_at_months, t);
                
                return (
                  <Card 
                    key={benefit.benefit_id}
                    className="border-l-4 border-l-gray-300 bg-gray-50"
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <Lock className="h-5 w-5 text-gray-400" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-600">{benefit.benefit_name}</h4>
                            {benefit.description && (
                              <p className="text-sm text-gray-400 mt-1">{benefit.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className={tierInfo.color}>
                                <tierInfo.icon className="h-3 w-3 mr-1" />
                                {tierInfo.name}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {t('benefits.unlocksAfter', { months: benefit.unlock_at_months })}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-gray-500">
                            <TrendingUp className="h-3 w-3 mr-1" />
                            {t('benefits.monthsToGo', { count: benefit.months_until_unlock })}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Usage History */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('tabs.usageHistory')}</CardTitle>
            </CardHeader>
            <CardContent>
              {usageHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{t('benefits.noUsageHistory')}</p>
                  <p className="text-sm text-gray-400">
                    {t('benefits.usageHistoryAppear')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {usageHistory.map((usage) => {
                    const Icon = BENEFIT_ICONS[usage.benefit_type] || Sparkles;
                    return (
                      <div 
                        key={usage.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                            <Icon className="h-4 w-4 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{usage.benefit_name}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(usage.used_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        {usage.value && (
                          <Badge variant="outline">
                            {usage.benefit_type === 'discount_percentage' ? `${usage.value}%` : 
                             usage.benefit_type === 'discount_fixed' ? `$${usage.value}` : usage.value}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MemberBenefits;
